import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { PageHeader, fmtDate, EmptyState } from "@/components/common";
import { Plus, Trash2, Search, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm";

const CATEGORIES = ["Task", "Team", "Client", "Approval", "Project", "Personal", "Business", "Meeting", "WhatsApp", "Delay", "Performance", "Other"];

export default function Memory() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "Other", tags: [], auto_summarize: true });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      const r = await api.get("/memories", { params });
      setRows(r.data?.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [category]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/memories", { ...form, tags: form.tags || [] });
      toast.success("Memory saved");
      setOpen(false);
      setForm({ title: "", content: "", category: "Other", tags: [], auto_summarize: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Delete memory", message: "Delete this memory?", confirmText: "Delete", danger: true }))) return;
    try {
      await api.delete(`/memories/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.message || "Failed to delete"); }
  };

  return (
    <div data-testid="memory-search-page">
      <PageHeader
        title="AI Memory"
        breadcrumbs={["AI Memory", "Search"]}
        actions={<button onClick={() => setOpen(true)} className="erp-btn-primary inline-flex items-center gap-2" data-testid="add-memory-btn"><Plus size={14}/> Save Memory</button>}
      />

      <div className="erp-card p-3 mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="erp-input pl-8" placeholder="Search memories..."
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            data-testid="memory-search-input"
          />
        </div>
        <select className="erp-input max-w-[200px]" value={category} onChange={e => setCategory(e.target.value)} data-testid="memory-category-filter">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={load} className="erp-btn-secondary" data-testid="memory-search-btn">Search</button>
      </div>

      <div className="card">
        <div className="card-head"><h3><span className="dot" />Saved Memories</h3><span className="text-xs text-slate-500">{rows.length} saved</span></div>
        <div className="card-body">
      {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline"/></div> :
       rows.length === 0 ? <EmptyState title="No memories" hint="Save your first memory to enable AI search & chat." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map(m => (
            <div key={m.id} className="erp-card p-4" data-testid={`memory-${m.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-heading font-semibold text-slate-900">{m.title}</div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <span className="badge-base bg-blue-50 text-blue-700 border-blue-200">{m.category}</span>
                    {(m.tags || []).slice(0, 4).map((t, i) => <span key={i} className="badge-base bg-slate-50 text-slate-600 border-slate-200">{t}</span>)}
                  </div>
                </div>
                <button onClick={() => remove(m.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded-sm"><Trash2 size={14}/></button>
              </div>
              <div className="text-sm text-slate-700 line-clamp-3">{m.summary || m.content}</div>
              <div className="text-[10px] text-slate-500 mt-2 uppercase tracking-wider">{fmtDate(m.created_at)} · by {m.user_name}</div>
            </div>
          ))}
        </div>
      )}
        </div>
      </div>

      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-xl" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-heading">Save Memory</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Title *</label>
                <input className="erp-input mt-1" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required data-testid="memory-title-input"/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Content *</label>
                <textarea rows={5} className="erp-input mt-1" value={form.content} onChange={e => setForm({...form, content: e.target.value})} required data-testid="memory-content-input"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Category</label>
                  <select className="erp-input mt-1" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Tags (comma-separated)</label>
                  <input className="erp-input mt-1" value={(form.tags || []).join(", ")} onChange={e => setForm({...form, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}/>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.auto_summarize} onChange={e => setForm({...form, auto_summarize: e.target.checked})}/>
                <span>Auto-summarize with Claude AI</span>
              </label>
              <DialogFooter>
                <button type="button" onClick={() => setOpen(false)} className="erp-btn-secondary">Cancel</button>
                <button type="submit" disabled={submitting} className="erp-btn-primary inline-flex items-center gap-2" data-testid="save-memory-btn">
                  {submitting && <Loader2 size={14} className="animate-spin"/>} Save
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
